import {Card, DataStatus, Label, openDialog} from 'platform/components';
import {Grid, Heading, Text, VStack} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {isNotNil, mergeAll} from 'ramda';

import {useGetRentalPriceGroupQuery, useGetRentalVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {DmsDataGrid, getPriceWithoutVat, useGetVatRatesOptions, useTenant} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import type {QueryFilterObject} from 'features/eag-grid';

import {ChangePriceGroupForm} from './ChangePriceGroupForm';

export function VehicleRentalPrices() {
  const {id: vehicleId} = useRequiredParams();
  const {tenantCurrency} = useTenant();
  const [vatOptions] = useGetVatRatesOptions();
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const formatCurrencyWithSymbol = useCurrencySymbolFormatter();

  const {data: rentalVehicle, isLoading: isLoadingVehicle} = useGetRentalVehicleQuery({
    vehicleId,
  });

  const {data: priceGroup, isLoading: isLoadingPriceGroup} = useGetRentalPriceGroupQuery(
    {
      rentalPriceGroupId: rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId ?? '',
    },
    {
      skip: !rentalVehicle?.rentalPriceGroup?.rentalPriceGroupId,
    }
  );

  const isLoading = isLoadingVehicle || isLoadingPriceGroup;

  const selectedItemId = rentalVehicle?.rentalPriceGroup?.rentalPriceGroupItemId;
  const selectedItem = priceGroup?.items?.find(
    (item) => item.rentalPriceGroupItemId === selectedItemId
  );
  const hasNoData = !rentalVehicle?.rentalPriceGroup || !priceGroup || !selectedItem;

  const periodLabel =
    selectedItem?.range.period === 'day'
      ? i18n.t('entity.rental.labels.day')
      : i18n.t('entity.rental.labels.month');

  const vatRate = vatOptions?.find((option) => option.type === priceGroup?.vatType);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {rentalVehicleId: vehicleId}]);

  const handleChangePriceGroup = () =>
    openDialog(<ChangePriceGroupForm vehicleId={vehicleId} />, {
      title: i18n.t('page.rentalOrder.labels.changePriceGroup'),
      size: 'large',
    });

  return (
    <VStack spacing={4}>
      <Card
        title={i18n.t('entity.rental.labels.rentalPrices')}
        actions={[
          {
            type: 'button',
            variant: 'link',
            onClick: handleChangePriceGroup,
            leftIcon: 'action/autorenew',
            title: i18n.t('page.rentalOrder.labels.changePriceGroup'),
            size: 'small',
          },
        ]}
      >
        <DataStatus
          isLoading={isLoading}
          isError={false}
          isEmpty={hasNoData}
          emptyMessage={i18n.t('entity.rental.labels.noPriceGroup')}
        >
          {isNotNil(selectedItem) && isNotNil(vatRate) && (
            <Grid columns={5}>
              <VStack spacing={1}>
                <Label>{i18n.t('entity.rental.labels.rate')}</Label>
                <Heading size={3}>
                  {formatCurrency(parseFloat(selectedItem.price.rate), tenantCurrency, 2)}
                </Heading>
                <Text size="small" color="tertiary">
                  {`${formatCurrency(
                    getPriceWithoutVat(selectedItem.price.rate, parseFloat(vatRate.rate)),
                    tenantCurrency,
                    2
                  )} ${i18n.t('general.labels.withoutVat')}`}
                </Text>
              </VStack>

              <VStack spacing={1}>
                <Label>{`${formatCurrencyWithSymbol(tenantCurrency)}${i18n.t('entity.rental.labels.ratePerKilometer')}`}</Label>
                <Heading size={3}>
                  {formatCurrency(
                    parseFloat(selectedItem.price.ratePerKilometer),
                    tenantCurrency,
                    2
                  )}
                </Heading>
                <Text size="small" color="tertiary">
                  {`${formatCurrency(
                    getPriceWithoutVat(
                      selectedItem.price.ratePerKilometer,
                      parseFloat(vatRate.rate)
                    ),
                    tenantCurrency,
                    2
                  )} ${i18n.t('general.labels.withoutVat')}`}
                </Text>
              </VStack>

              <VStack spacing={1}>
                <Label>{i18n.t('entity.rental.labels.includedKilometers')}</Label>
                <Heading size={3}>
                  {`${formatNumber(selectedItem.price.includedKilometers, 2)} ${i18n.t('general.metric.km')}`}
                </Heading>
              </VStack>

              <VStack spacing={1}>
                <Label>{i18n.t('general.labels.type')}</Label>
                <Heading size={3}>{periodLabel}</Heading>
              </VStack>

              <VStack spacing={1}>
                <Label>{i18n.t('general.labels.vat')}</Label>
                <Heading size={3}>{vatRate.label}</Heading>
              </VStack>
            </Grid>
          )}
        </DataStatus>
      </Card>

      <Card title={i18n.t('entity.vehicle.labels.priceHistory')}>
        <DmsDataGrid gridCode="rental-vehicle-price-log" autoHeight queryModifier={queryModifier} />
      </Card>
    </VStack>
  );
}
